import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, GripVertical } from 'lucide-react';
import { AdminOnly } from '@/components/AdminOnly';
import type { Project } from '@/hooks/useProjects';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface ProjectKanbanCardProps {
  project: Project;
}

export function ProjectKanbanCard({ project }: ProjectKanbanCardProps) {
  const navigate = useNavigate();
  const { formatMoney } = useCompanyLocale();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const jobCount = project.project_jobs?.length ?? 0;
  const totalRevenue = project.project_jobs?.reduce(
    (sum, pj) => sum + (pj.job?.invoice_total ?? 0),
    0
  ) ?? 0;

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className="group cursor-pointer hover:shadow-md transition-shadow border border-border"
        onClick={() => navigate(`/projects/${project.id}`)}
      >
        <CardContent className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-1">
            <p className="text-sm font-medium leading-tight line-clamp-2 flex-1">{project.name}</p>
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity focus:outline-none mt-0.5"
              onClick={e => e.stopPropagation()}
              aria-label="Drag to move"
            >
              <GripVertical className="h-4 w-4" />
            </button>
          </div>

          {jobCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Briefcase className="h-3 w-3" />
              <span>{jobCount} job{jobCount !== 1 ? 's' : ''}</span>
              {totalRevenue > 0 && (
                <AdminOnly>
                  <span className="ml-auto text-success font-medium">{formatMoney(totalRevenue)}</span>
                </AdminOnly>
              )}
            </div>
          )}

          {project.project_jobs?.slice(0, 2).map(pj => (
            <Badge key={pj.id} variant="outline" className="text-[10px] h-5 font-mono">
              {pj.job?.job_number ?? '—'}
            </Badge>
          ))}
          {jobCount > 2 && (
            <Badge variant="outline" className="text-[10px] h-5">+{jobCount - 2} more</Badge>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
